import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, ArrowRight, Search, Phone } from 'lucide-react';
import { BLOG_POSTS, PHONES } from '../content';
import PageHero from '../components/PageHero';
import { SEOHead } from '../components/SEOHead';

const categories = ['Tümü', 'Hizmetler', 'İpuçları', 'Haberler', 'Güvenlik', 'Bakım'];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('Tümü');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = BLOG_POSTS.filter(post => {
    const matchCat = activeCategory === 'Tümü' || post.category === activeCategory;
    const matchSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <main data-testid="blog-page" style={{ background: 'var(--bg)' }}>
      <SEOHead path="/blog" />
      <PageHero
        breadcrumb="Blog"
        headline="Lastik Rehberi."
        accentText="Bilmeniz Gerekenler."
        sub="Güvenli sürüş için pratik ipuçları, bakım bilgileri ve güncel haberler."
        badge="Uzman İçerikleri"
      />

      {/* ─── FILTERS ─── */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '1.25rem 0' }}>
        <div className="wrap">
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
              <Search size={15} style={{ position: 'absolute', left: '.875rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text2)' }} />
              <input
                data-testid="blog-search"
                type="text"
                placeholder="Yazılarda ara..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', background: 'var(--card)',
                  border: '1px solid var(--border)', borderRadius: 'var(--r)',
                  padding: '.625rem .875rem .625rem 2.25rem',
                  color: 'var(--text)', fontSize: '.85rem', outline: 'none',
                  transition: 'border-color .2s',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            {/* Categories */}
            <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`blog-filter-${cat}`}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    background: activeCategory === cat ? 'var(--blue)' : 'var(--card)',
                    border: `1px solid ${activeCategory === cat ? 'var(--blue)' : 'var(--border)'}`,
                    borderRadius: 100, padding: '.375rem .875rem',
                    color: activeCategory === cat ? '#fff' : 'var(--text2)',
                    fontSize: '.8rem', fontWeight: 500, cursor: 'pointer',
                    transition: 'all .2s',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── BLOG GRID ─── */}
      <section style={{ padding: '3rem 0' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2.5rem', alignItems: 'start' }} className="blog-layout">
            {/* Main grid */}
            <div>
              {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text2)', background: 'var(--surface)', borderRadius: 'var(--rl)', border: '1px solid var(--border)' }}>
                  <p>Aramanızla eşleşen yazı bulunamadı.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
                  {filtered.map((post, i) => (
                    <motion.article
                      key={post.id}
                      data-testid={`blog-list-card-${post.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: .4, delay: i * .05 }}
                      className="blog-card"
                      style={{
                        background: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--rl)',
                        overflow: 'hidden',
                      }}
                    >
                      <Link to={`/blog/${post.slug}`} style={{ display: 'block' }}>
                        <div className="blog-img" style={{ height: 200 }}>
                          <img src={post.image} alt={post.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      </Link>
                      <div style={{ padding: '1.125rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.625rem' }}>
                          <span style={{
                            background: 'var(--blue-dim)', border: '1px solid var(--blue-border)',
                            borderRadius: 100, padding: '.175rem .625rem',
                            fontSize: '.68rem', color: 'var(--blue)', fontWeight: 600,
                          }}>{post.category}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '.72rem', color: 'var(--text2)' }}>
                            <CalendarDays size={11} /> {post.date}
                          </span>
                        </div>
                        <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                          <h3 style={{
                            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                            fontSize: '.95rem', color: 'var(--text)', lineHeight: 1.4,
                            marginBottom: '.5rem', transition: 'color .2s',
                          }}
                            onMouseEnter={e => e.target.style.color = 'var(--blue)'}
                            onMouseLeave={e => e.target.style.color = 'var(--text)'}
                          >{post.title}</h3>
                        </Link>
                        <p style={{ color: 'var(--text2)', fontSize: '.82rem', lineHeight: 1.6, marginBottom: '.75rem' }}>
                          {post.excerpt}
                        </p>
                        <Link to={`/blog/${post.slug}`} style={{
                          display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                          color: 'var(--blue)', fontSize: '.82rem', fontWeight: 600, textDecoration: 'none',
                        }}>
                          Devamını Oku <ArrowRight size={12} />
                        </Link>
                      </div>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar CTA */}
            <div className="blog-sidebar" style={{ position: 'sticky', top: 100 }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(229,51,51,.08) 0%, var(--card) 100%)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rl)',
                padding: '2rem',
                textAlign: 'center',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'var(--red)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto .875rem',
                  boxShadow: '0 6px 24px rgba(229,51,51,.3)',
                }}>
                  <Phone size={20} style={{ color: '#fff' }} />
                </div>
                <h3 style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                  fontSize: '1.05rem', color: 'var(--text)', marginBottom: '.35rem',
                }}>Yolda mı Kaldınız?</h3>
                <p style={{ fontSize: '.82rem', color: 'var(--text2)', lineHeight: 1.6, marginBottom: '1rem' }}>
                  Okumaya devam edin ama acil bir durum varsa hemen arayın.
                </p>
                <a
                  href={PHONES.main_tel}
                  data-testid="blog-sidebar-call"
                  className="btn btn-primary num"
                  style={{ width: '100%', justifyContent: 'center', gap: '.5rem', fontSize: '.88rem' }}
                >
                  <Phone size={14} /> Hemen Ara
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media(max-width:900px){
          .blog-layout{grid-template-columns:1fr!important}
          .blog-sidebar{position:static!important;order:-1}
        }
      `}</style>
    </main>
  );
}
